/** CMS contracts contain data only. No browser, database or framework dependencies. */
import { STATIC_PAGES, NON_INDEXABLE_PRERENDER_PAGES, CATEGORY_PAGE_PATHS, DISTRICT_SLUGS, NEARBY_SLUGS, canonicalPath, isBouquetCreatorPath } from './index.js';

export type CmsData = Record<string, string>;
export interface CmsField {
  key: string;
  label: string;
  section: string;
  kind: 'text' | 'textarea' | 'image' | 'alt';
  defaultValue: string;
  maxLength?: number;
  required?: boolean;
  /** Slideshow/accordion content that is not present in every static snapshot. */
  optionalMarker?: boolean;
}
export interface CmsSchema {
  pageId: string;
  path: string;
  title: string;
  schemaVersion: 1;
  templateVersion: string;
  robots: string;
  noSuffix?: boolean;
  fields: CmsField[];
}
export interface CmsSnapshot {
  pageId: string;
  path: string;
  revision: string;
  schemaVersion: 1;
  templateVersion: string;
  data: CmsData;
  preview?: boolean;
  publishedAt?: string;
}
export interface CmsRevision { id: string; data: CmsData; createdAt: string; author: string; templateVersion?: string; schemaVersion?: number }
export interface CmsPage { id: string; path: string; title: string; group: string; supported: boolean }
export interface PageSummary extends CmsPage {
  status: 'unavailable' | 'draft' | 'published' | 'changed';
  version: number;
  publishedAt?: string;
}
export interface PageDetail {
  page: PageSummary;
  schema: CmsSchema;
  version: number;
  draft: CmsRevision;
  published: CmsRevision | null;
  previous: CmsRevision | null;
}

const TITLES: Record<string, string> = {
  '/': 'Strona główna', '/platnosci/': 'Płatności', '/kontakt/': 'Kontakt', '/o-nas/': 'O nas',
  '/faq/': 'Najczęstsze pytania', '/dostawa/': 'Dostawa', '/dla-firm/': 'Dla firm',
  '/slub/': 'Ślub', '/regulamin/': 'Regulamin', '/prywatnosc/': 'Polityka prywatności',
  '/polityka-zwrotow/': 'Polityka zwrotów', '/warsztaty-florystyczne/': 'Warsztaty florystyczne',
  '/zamowienie-specjalne/': 'Zamówienie specjalne', '/kwiaciarnia-lublin/': 'Kwiaciarnia Lublin',
  '/kwiaciarnia-okolice/': 'Kwiaciarnia — okolice Lublina',
};
const paths = [...new Set([
  ...STATIC_PAGES.map(p => p.path), ...NON_INDEXABLE_PRERENDER_PAGES.map(p => p.path),
  ...CATEGORY_PAGE_PATHS, '/dla-kogo/',
  ...DISTRICT_SLUGS.map(s => `/kwiaciarnia-lublin/${s}/`),
  ...NEARBY_SLUGS.map(s => `/kwiaciarnia-okolice/${s}/`),
])].filter(p => p !== '/blog/' && !isBouquetCreatorPath(p));
export const CMS_PAGES: CmsPage[] = paths.map(path => ({
  id: path === '/' ? 'home' : path.replace(/^\/|\/$/g, '').replace(/\//g, '--'),
  path,
  title: TITLES[path] ?? path.replace(/^\/|\/$/g, '').split('/').at(-1)!.replace(/-/g, ' '),
  group: path === '/' ? 'Strona główna' : path.startsWith('/kwiaciarnia-') ? 'Strony lokalne' : TITLES[path] ? 'Strony informacyjne' : 'Kategorie',
  supported: true,
}));
export function cmsPageById(id: string): CmsPage | undefined { return CMS_PAGES.find(p => p.id === id); }
export function cmsPageByPath(path: string): CmsPage | undefined { return CMS_PAGES.find(p => p.path === canonicalPath(path)); }
/** Staged rollout: opt in exact page IDs; omission enables only the payment pilot. */
export function cmsPageEnabled(id: string, configuredIds?: string): boolean {
  const configured = configuredIds === undefined ? 'platnosci' : configuredIds.trim();
  return configured === '*' || configured.split(',').map(value => value.trim()).includes(id);
}
export function defaultsForSchema(schema: CmsSchema): CmsData { return Object.fromEntries(schema.fields.map(f => [f.key, f.defaultValue])); }
export function serializeCmsJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
}
/** Stable schema identity, including defaults to prevent silently reverting new code defaults. */
export function cmsTemplateVersion(fields: CmsField[], robots: string, noSuffix = false): string {
  const input = JSON.stringify({ fields: [...fields].sort((a, b) => a.key.localeCompare(b.key)), robots, noSuffix });
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) hash = Math.imul(hash ^ input.charCodeAt(i), 16777619);
  return `v1-${(hash >>> 0).toString(16)}`;
}
export function validateCmsData(schema: CmsSchema, input: unknown): CmsData {
  if (!input || typeof input !== 'object' || Array.isArray(input)) throw new Error('Nieprawidłowa treść strony.');
  const data = input as Record<string, unknown>;
  if (Object.keys(data).length !== schema.fields.length) throw new Error('Nieprawidłowy zestaw pól.');
  const result: CmsData = {};
  for (const field of schema.fields) {
    if (!Object.prototype.hasOwnProperty.call(data, field.key) || typeof data[field.key] !== 'string') throw new Error(`Brak pola: ${field.label}`);
    const value = data[field.key] as string;
    const limit = field.maxLength ?? (field.kind === 'textarea' ? 20000 : field.kind === 'image' ? 2048 : 500);
    if (value.length > limit || /[\u0000-\u0008\u000b\u000c\u000e-\u001f]/.test(value)) throw new Error(`Nieprawidłowa długość pola: ${field.label}`);
    if (field.required && !value.trim()) throw new Error(`Uzupełnij pole: ${field.label}`);
    if (field.kind === 'image' && value !== field.defaultValue && value !== '' && !/^\/api\/page-media\/[0-9a-f-]{36}$/.test(value)) throw new Error('Wybierz obraz z biblioteki panelu.');
    if (field.key === '__proto__' || field.key === 'constructor' || field.key === 'prototype') throw new Error('Niedozwolone pole.');
    result[field.key] = value;
  }
  return result;
}
export function cmsMediaIds(data: CmsData): string[] {
  return [...new Set(Object.values(data).flatMap(v => /^\/api\/page-media\/([0-9a-f-]{36})$/.exec(v)?.slice(1) ?? []))];
}

export function readCmsTemplate(id: string, html: string): CmsSchema {
  const page = cmsPageById(id);
  if (!page) throw new Error('Unknown page');
  const fields = new Map<string, CmsField>();
  let robots = 'index, follow';
  let noSuffix = false;
  for (const match of html.matchAll(/<script\b([^>]*\bdata-cms-fields="1"[^>]*)>([\s\S]*?)<\/script>/g)) {
    const entries = JSON.parse(match[2]) as CmsField[];
    if (!Array.isArray(entries)) throw new Error('Invalid fields');
    for (const field of entries) {
      if (!/^[a-zA-Z][a-zA-Z0-9._-]{0,149}$/.test(field.key) || typeof field.defaultValue !== 'string') throw new Error('Invalid field');
      const previous = fields.get(field.key);
      if (previous && JSON.stringify(previous) !== JSON.stringify(field)) throw new Error(`Duplicate field: ${field.key}`);
      fields.set(field.key, field);
    }
    const robotMatch = /data-cms-robots="([^"]+)"/.exec(match[1]);
    if (robotMatch) robots = robotMatch[1];
    if (/data-cms-no-suffix="true"/.test(match[1])) noSuffix = true;
  }
  if (![...fields.keys()].some(key => !key.startsWith('seo.')) || !['seo.title', 'seo.description', 'seo.image'].every(key => fields.has(key))) throw new Error('Page template is not CMS-ready');
  const values = [...fields.values()];
  for (const field of values) {
    if (field.key.startsWith('seo.') || field.optionalMarker) continue;
    const attribute = field.kind === 'image' ? 'image' : field.kind === 'alt' ? 'alt' : 'text';
    if (!html.includes(`data-cms-${attribute}="${field.key}"`)) throw new Error(`Missing template field: ${field.key}`);
  }
  return { pageId: page.id, path: page.path, title: page.title, schemaVersion: 1, templateVersion: cmsTemplateVersion(values, robots, noSuffix), fields: values, robots, noSuffix };
}
